/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Locale-aware comparison that treats digit runs as numbers.
 * Use as an ag-grid column comparator so values like X1-9 sort before X1-89.
 */
const LEADING_NUMBER = /^(-?\d+(?:\.\d+)?)(.*)$/;

/**
 * A leading number is compared as a number, then the remainder as text, so 1.35, 1.5, and 1.5a
 * stay in one order. Codes that do not start with a number, such as X1-9, stay on natural text.
 */
export function compareGridValue(left: unknown, right: unknown): number {
  const leftPart = leadingNumber(left);
  const rightPart = leadingNumber(right);
  if (leftPart !== null && rightPart !== null) {
    if (leftPart.number !== rightPart.number) {
      return leftPart.number - rightPart.number;
    }
    return compareNaturalText(leftPart.rest, rightPart.rest);
  }
  return compareNaturalText(left, right);
}

function leadingNumber(value: unknown): {number: number; rest: string} | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? {number: value, rest: ''} : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const match = LEADING_NUMBER.exec(value.trim());
  if (match === null) {
    return null;
  }
  return {number: Number(match[1]), rest: match[2]};
}

export function compareNaturalText(left: unknown, right: unknown): number {
    return String(left ?? '').localeCompare(String(right ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base'
    });
}
